import { AppDownloadBanner, UserProfile } from '_atoms';
import React, { useEffect, useState } from 'react';
import { FaEnvelopeOpenText, FaSkating } from 'react-icons/fa';
import { TiTicket } from 'react-icons/ti';

import { useAppSelector } from '_hooks';
import { BsPass } from 'react-icons/bs';
import {
  MdAttachMoney,
  MdCake,
  MdCelebration,
  MdClose,
  MdEdit,
  MdEmail,
  MdFilePresent,
  MdHistory,
  MdInventory2,
  MdLocalOffer,
  MdMenu,
  MdPeople,
  MdPieChart,
  MdPerson,
  MdSettings,
  MdSportsHockey,
} from 'react-icons/md';

import { SeasonStatisticsBanner } from '_molecules';
import { Link, useLocation } from 'react-router-dom';
import main_logo from '../assets/logos/main_logo.png';

interface AccountLayoutProps {
  children: React.ReactNode;
}

/** Matches Mobile booking type icon tiles (tint + accent). */
const purchaseTones = {
  tickets: { accent: '#295F94', tint: '#DBEAFE' },
  lessons: { accent: '#0F766E', tint: '#CCFBF1' },
  hockey: { accent: '#C2410C', tint: '#FFEDD5' },
  passes: { accent: '#6D28D9', tint: '#EDE9FE' },
  parties: { accent: '#BE185D', tint: '#FCE7F3' },
  waivers: { accent: '#374151', tint: '#F3F4F6' },
};

const AccountLayout: React.FC<AccountLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const LOCAL_STORAGE_KEY = 'accountLayoutCollapsedSections';

  const getInitialCollapsedSections = () => {
    if (typeof window === 'undefined') {
      return {};
    }

    try {
      const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          return parsed as Record<string, boolean>;
        }
      }
    } catch (error) {
      console.error('Failed to parse collapsed sections from storage:', error);
    }

    return {};
  };

  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >(getInitialCollapsedSections);
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify(collapsedSections),
      );
    } catch (error) {
      console.error('Failed to store collapsed sections:', error);
    }
  }, [collapsedSections]);

  // Get user data from Redux store
  const { accountDetails: accountDetailsState } = useAppSelector(
    (state: any) => state.auth,
  );

  const isActive = (path: string) => {
    if (path === '/admin/user-accounts') {
      return (
        location.pathname === path ||
        location.pathname.startsWith('/admin/user-accounts/')
      );
    }
    return location.pathname === path;
  };

  const toggleSection = (sectionTitle: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }));
  };

  const navLinkClass = (path: string) => {
    const baseClasses =
      'relative text-md font-medium transition-all duration-200 ease-in-out px-3 py-2';
    const activeClasses = isActive(path)
      ? 'text-blue-500 border-b-2 border-blue-500'
      : 'text-gray-700 hover:text-blue-500 hover:bg-blue-50';
    return `${baseClasses} ${activeClasses}`;
  };

  const mobileNavLinkClass = (path: string) => {
    const baseClasses =
      'block px-6 py-4 text-md font-medium transition-all duration-200 ease-in-out border-l-4';
    const activeClasses = isActive(path)
      ? 'text-blue-500 bg-blue-50 border-blue-500'
      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50 border-transparent';
    return `${baseClasses} ${activeClasses}`;
  };

  // Function to check if user has access to a navigation category
  const hasAccessToCategory = (category: string) => {
    if (!accountDetailsState?.roleKey) return false;

    const userRole = accountDetailsState.roleKey;

    switch (category) {
      case 'account-only':
        // All authenticated users can access account-only items
        return true;
      case 'moderator-only':
        // Moderators, staff, admins, and super_admins can access moderator items
        return ['moderator', 'staff', 'admin', 'super_admin'].includes(
          userRole,
        );
      case 'admin-only':
        // Only admins and super_admins can access admin items
        return ['admin', 'super_admin'].includes(userRole);
      default:
        return false;
    }
  };

  const navGroups = [
    {
      title: 'My Purchases',
      category: 'account-only',
      items: [
        {
          path: '/my-account/booked-skating-sessions',
          name: 'Tickets',
          icon: <TiTicket size={20} />,
          category: 'account-only',
          tone: purchaseTones.tickets,
        },
        {
          path: '/my-account/booked-skating-lessons',
          name: 'Skating Lessons',
          icon: <FaSkating size={20} />,
          category: 'account-only',
          tone: purchaseTones.lessons,
        },
        {
          path: '/my-account/booked-hockey-lessons',
          name: 'Hockey Lessons',
          icon: <MdSportsHockey size={20} />,
          category: 'account-only',
          tone: purchaseTones.hockey,
        },
        {
          path: '/my-account/booked-skating-passes',
          name: 'Skating Passes',
          icon: <BsPass size={20} />,
          category: 'account-only',
          tone: purchaseTones.passes,
        },
        {
          path: '/my-account/booked-parties',
          name: 'Parties',
          icon: <MdCelebration size={20} />,
          category: 'account-only',
          tone: purchaseTones.parties,
        },
        {
          path: '/my-account/submitted-waivers',
          name: 'My Waivers',
          icon: <MdFilePresent size={20} />,
          category: 'account-only',
          tone: purchaseTones.waivers,
        },
      ],
    },
    {
      title: 'Account',
      category: 'account-only',
      items: [
        {
          path: '/my-account/profile',
          name: 'Profile',
          icon: <MdPerson size={20} className="text-gray-900" />,
          category: 'account-only',
        },
      ],
    },
    {
      title: 'Session Management',
      category: 'moderator-only',
      items: [
        {
          path: '/admin/booked-skating-sessions',
          name: 'Tickets',
          icon: <TiTicket size={20} className="text-blue-500" />,
          category: 'moderator-only',
        },
        {
          path: '/admin/user-accounts',
          name: 'Customers',
          icon: <MdPeople size={20} className="text-blue-500" />,
          category: 'moderator-only',
        },
        {
          path: '/admin/customer-waivers',
          name: 'Waivers',
          icon: <MdFilePresent size={20} className="text-blue-500" />,
          category: 'moderator-only',
        },
        {
          path: '/admin/booked-parties',
          name: 'Parties',
          icon: <MdCelebration size={20} className="text-blue-500" />,
          category: 'moderator-only',
        },
        {
          path: '/admin/booked-skating-passes',
          name: 'Passes - 10 Pack/Season',
          icon: <BsPass size={20} className="text-blue-500" />,
          category: 'moderator-only',
        },
        {
          path: '/admin/booked-skating-lessons',
          name: 'Lessons',
          icon: <FaSkating size={20} className="text-blue-500" />,
          category: 'moderator-only',
        },
        {
          path: '/admin/booked-hockey-lessons',
          name: 'Hockey',
          icon: <MdSportsHockey size={20} className="text-blue-500" />,
          category: 'moderator-only',
        },
      ],
    },
    {
      title: 'Admin Only Settings',
      category: 'admin-only',
      items: [
        {
          path: '/admin/coupon-management',
          name: 'Coupon Management',
          icon: <MdLocalOffer size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/ticket-audit-log',
          name: 'Ticket Audit Log',
          icon: <MdHistory size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/order-sources',
          name: 'Order Sources',
          icon: <MdPieChart size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/skating-sessions',
          name: 'Tickets Settings',
          icon: <TiTicket size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/skating-lessons',
          name: 'Skating Lessons',
          icon: <FaSkating size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/hockey-lessons',
          name: 'Hockey Lessons',
          icon: <MdSportsHockey size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/skating-passes',
          name: 'Skating Passes',
          icon: <BsPass size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/season-settings',
          name: 'Season Settings',
          icon: <MdSettings size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/season-price-list',
          name: 'Season Price List',
          icon: <MdAttachMoney size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/parties-settings',
          name: 'Parties Settings',
          icon: <MdCake size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/waiver',
          name: 'Waiver',
          icon: <MdEdit size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/archived-waivers',
          name: 'Archived Waivers',
          icon: <MdInventory2 size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/emails',
          name: 'Emails',
          icon: <MdEmail size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
        {
          path: '/admin/contact-us-messages',
          name: 'Messages',
          icon: <FaEnvelopeOpenText size={20} className="text-purple-500" />,
          category: 'admin-only',
        },
      ],
    },
  ];

  // Filter navigation groups based on user role
  const filteredNavGroups = navGroups.filter(group =>
    hasAccessToCategory(group.category),
  );

  return (
    <div className="flex min-h-[calc(100vh-var(--qa-banner-offset,0px))] bg-gray-50">
      {/* Desktop Sidebar Toggle Button */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className={`hidden lg:flex fixed top-[calc(1rem+var(--qa-banner-offset,0px))] z-[60] items-center justify-center w-8 h-16 bg-blue-600 text-white rounded-r-lg shadow-lg hover:bg-blue-700 transition-all duration-300 ${
          sidebarCollapsed ? 'left-0' : 'left-64'
        }`}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <span className="text-lg">{sidebarCollapsed ? '▶' : '◀'}</span>
      </button>

      {/* Sidebar — h-svh keeps the drawer above iOS Safari's bottom URL bar */}
      <div
        className={`fixed top-[var(--qa-banner-offset,0px)] left-0 z-40 h-[calc(100svh-var(--qa-banner-offset,0px))] max-h-[calc(100svh-var(--qa-banner-offset,0px))] bg-white shadow-lg transform transition-all duration-300 ease-in-out w-64 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${sidebarCollapsed ? 'lg:-translate-x-64' : 'lg:translate-x-0'}`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img src={main_logo} alt="logo" className="w-12" />
            <h1 className="text-sm md:text-md font-bold text-gray-900">
              My Dashboard
            </h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md hover:bg-gray-100"
          >
            <MdClose size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 mt-6 px-4 pb-6 overflow-y-auto custom-scrollbar">
          <div className="space-y-2">
            {filteredNavGroups.map(group => {
              const isCollapsed = collapsedSections[group.title];
              return (
                <div key={group.title}>
                  <button
                    onClick={() => toggleSection(group.title)}
                    className="w-full flex items-center justify-between px-3 py-2 mb-2 text-sm font-medium text-gray-700 bg-blue-50 hover:bg-blue-100 hover:text-gray-900 transition-all duration-200 rounded-md group"
                  >
                    <span className="text-md font-bold">{group.title}</span>
                    <svg
                      className={`w-6 h-6 font-bold text-blue-400 group-hover:text-blue-600 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div
                    className={`space-y-1 transition-all duration-300 ease-in-out overflow-hidden ${
                      isCollapsed
                        ? 'max-h-0 opacity-0'
                        : 'max-h-200 opacity-100'
                    }`}
                  >
                    {group.items.map(item => {
                      const tone = 'tone' in item ? item.tone : undefined;
                      const active = isActive(item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                            active
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <span
                            className={`flex items-center justify-center ${
                              tone
                                ? 'h-8 w-8 rounded-lg'
                                : active
                                  ? 'text-blue-600'
                                  : 'text-gray-500'
                            }`}
                            style={
                              tone
                                ? {
                                    backgroundColor: tone.tint,
                                    color: tone.accent,
                                  }
                                : undefined
                            }
                          >
                            {item.icon}
                          </span>
                          <span className="font-medium">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile Navigation Links */}
          <div className="block md:hidden pt-2 pb-6">
            <button
              onClick={() => toggleSection('Quick Links')}
              className="w-full flex items-center justify-between px-3 py-2 mb-2 text-sm font-medium text-gray-700 bg-blue-50 hover:bg-blue-100 hover:text-gray-900 transition-all duration-200 rounded-md group"
            >
              <span className="text-md font-bold">Quick Links</span>
              <svg
                className={`w-6 h-6 font-bold text-blue-400 group-hover:text-blue-600 transition-transform duration-200 ${collapsedSections['Quick Links'] ? '' : 'rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                collapsedSections['Quick Links']
                  ? 'max-h-0 opacity-0'
                  : 'max-h-[800px] opacity-100'
              }`}
            >
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/tickets"
                    className={mobileNavLinkClass('/')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    Tickets
                  </Link>
                </li>
                <li>
                  <Link
                    to="/passes"
                    className={mobileNavLinkClass('/passes')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    Passes
                  </Link>
                </li>
                <li>
                  <Link
                    to="/parties"
                    className={mobileNavLinkClass('/parties')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    Parties
                  </Link>
                </li>
                <li>
                  <Link
                    to="/lessons"
                    className={mobileNavLinkClass('/lessons')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    Lessons
                  </Link>
                </li>
                <li>
                  <Link
                    to="/hockey"
                    className={mobileNavLinkClass('/hockey')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    Hockey
                  </Link>
                </li>
                <li>
                  <Link
                    to="/admin/season-settings"
                    className={mobileNavLinkClass('/admin/season-settings')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    My Account
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact-us"
                    className={mobileNavLinkClass('/contact-us')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/waivers"
                    className={mobileNavLinkClass('/waivers')}
                    onClick={() => setSidebarOpen(false)}
                  >
                    Waivers
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </nav>

        {/* Mobile store download — pinned above browser chrome / home indicator */}
        <div
          className="md:hidden flex-shrink-0 flex justify-center px-4 pt-3 border-t border-gray-200"
          style={{
            paddingBottom:
              'max(1.25rem, calc(env(safe-area-inset-bottom, 0px) + 0.75rem))',
          }}
        >
          <AppDownloadBanner />
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-64'
        }`}
      >
        {/* Top Bar */}
        <header className="sticky top-[var(--qa-banner-offset,0px)] z-50 bg-white shadow-sm border-b border-gray-200 h-16 flex items-center justify-between px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 flex items-center space-x-2"
          >
            {/* <img src={main_logo} alt="logo" className="block md:hidden w-12" /> */}

            <MdMenu size={24} className="text-gray-600" />
          </button>

          <div className="container mx-auto p-2 flex items-center justify-center sm:max-w-[90%] md:max-w-2xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl">
            <img src={main_logo} alt="logo" className="block md:hidden w-16" />

            <ul className="hidden md:flex space-x-2 items-center">
              <li>
                <Link to="/tickets" className={navLinkClass('/tickets')}>
                  Tickets
                </Link>
              </li>
              <li>
                <Link to="/passes" className={navLinkClass('/passes')}>
                  Passes
                </Link>
              </li>
              <li>
                <Link to="/parties" className={navLinkClass('/parties')}>
                  Parties
                </Link>
              </li>
              <li>
                <Link to="/lessons" className={navLinkClass('/lessons')}>
                  Lessons
                </Link>
              </li>
              <li>
                <Link to="/hockey" className={navLinkClass('/hockey')}>
                  Hockey
                </Link>
              </li>
              <li>
                <Link to="/waivers" className={navLinkClass('/waivers')}>
                  Sign Waiver
                </Link>
              </li>
              <li>
                <Link to="/contact-us" className={navLinkClass('/contact-us')}>
                  Contact Us
                </Link>
              </li>
            </ul>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {/* <IoIosMenu size={28} className="text-gray-700" /> */}
            </button>
          </div>

          <UserProfile />
        </header>

        {/* Statistics Banner */}
        <SeasonStatisticsBanner userRole={accountDetailsState?.roleKey} />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <div className="min-w-7xl mx-auto">{children}</div>
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AccountLayout;
